import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Save } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type NodeType, type TranscodeSettings } from "../api";
import { ErrorNotice, LoadingRows, formatTime } from "../components";

const bitrates = [8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160];

export function TranscodeSettingsPage({ role }: { role: string }) {
  const client = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const settings = useQuery({
    queryKey: ["transcode-settings"],
    queryFn: () => api<TranscodeSettings>("/admin/v1/transcode-settings"),
  });
  const nodeTypes = useQuery({
    queryKey: ["node-types"],
    queryFn: () => api<NodeType[]>("/admin/v1/node-types"),
  });
  const update = useMutation({
    mutationFn: (body: { mp3_bitrate_kbps: number; default_node_type: string; dsp_config: TranscodeSettings["dsp_config"]; reason: string }) =>
      api<TranscodeSettings>("/admin/v1/transcode-settings", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: async (data) => {
      setFeedback(`转码与 DSP 默认参数已更新，新创建任务将使用版本 ${data.version}。`);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["transcode-settings"] }),
        client.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    const data = Object.fromEntries(new FormData(event.currentTarget));
    update.mutate({
      mp3_bitrate_kbps: Number(data.mp3_bitrate_kbps),
      default_node_type: String(data.default_node_type),
      dsp_config: {
        ns_level: Number(data.ns_level),
        target_lufs: Number(data.target_lufs),
        max_gain_db: Number(data.max_gain_db),
        limiter_threshold: Number(data.limiter_threshold),
      },
      reason: String(data.reason),
    });
  };

  return <div className="page">
    <header className="page-heading"><div><p className="eyebrow">CONFIGURATION / TRANSCODE</p><h1>转码设置</h1><p>统一控制新任务的默认节点、输出与 DSP 参数；已创建任务不受影响。</p></div></header>
    {(settings.error || nodeTypes.error || update.error) && <ErrorNotice error={settings.error ?? nodeTypes.error ?? update.error} />}
    {feedback && <div className="notice notice--positive" role="status"><CheckCircle2 size={17} />{feedback}</div>}
    {(settings.isPending || nodeTypes.isPending) ? <LoadingRows columns={2} /> : settings.data && <form className="transcode-settings" key={settings.data.version} onSubmit={submit}>
      <section className="transcode-settings__current" aria-label="当前生效设置">
        <p className="eyebrow">当前生效</p>
        <strong>V{settings.data.version}</strong>
        <p>{settings.data.mp3_bitrate_kbps} kbps · {nodeTypeName(nodeTypes.data, settings.data.default_node_type)} · NS {settings.data.dsp_config.ns_level}</p>
        <time>更新于 {formatTime(settings.data.updated_at)}</time>
      </section>
      <section className="transcode-settings__editor">
        <div><p className="eyebrow">修改设置</p><h2>新任务默认参数</h2><p>保存后生成不可变版本。参数在任务创建时固化，排队中与执行中的任务不受影响。</p></div>
        {role === "ADMIN" ? <div className="transcode-settings__fields">
          <label className="field">默认节点类型<select name="default_node_type" defaultValue={settings.data.default_node_type}>{nodeTypes.data?.map((type) => <option key={type.id} value={type.id}>{type.display_name} · {type.ready_nodes} READY / {type.free_slots} 空闲槽位</option>)}</select><span className="field__hint">创建任务未传 node_type 时使用；已创建任务不会迁移。</span></label>
          <label className="field">MP3 CBR 码率<select name="mp3_bitrate_kbps" defaultValue={settings.data.mp3_bitrate_kbps}>{bitrates.map((rate) => <option key={rate} value={rate}>{rate} kbps</option>)}</select><span className="field__hint">默认及推荐值为 32 kbps。</span></label>
          <div className="dsp-fields">
            <label className="field">降噪等级<select name="ns_level" defaultValue={settings.data.dsp_config.ns_level}><option value="0">0 · 轻度</option><option value="1">1 · 中等</option><option value="2">2 · 强力（推荐）</option></select><span className="field__hint">越高抑制越强，复杂噪声下也更容易损伤语音边缘。</span></label>
            <label className="field">目标响度（LUFS）<input name="target_lufs" type="number" min="-24" max="-12" step="0.5" defaultValue={settings.data.dsp_config.target_lufs} required /><span className="field__hint">推荐 -18；数值越接近 0，成品听感越响。</span></label>
            <label className="field">最大补偿增益（dB）<input name="max_gain_db" type="number" min="0" max="12" step="0.5" defaultValue={settings.data.dsp_config.max_gain_db} required /><span className="field__hint">推荐 4；上限过大会同步放大残余底噪。</span></label>
            <label className="field">Limiter 阈值<input name="limiter_threshold" type="number" min="0.5" max="0.95" step="0.01" defaultValue={settings.data.dsp_config.limiter_threshold} required /><span className="field__hint">推荐 0.75；用于限制瞬态峰值、防止削波。</span></label>
          </div>
          <p className="transcode-settings__note">高通、EQ、压缩器及噪声感知 AGC 策略由音频核心固定维护，不开放为运营参数。</p>
          <label className="field">变更原因<textarea name="reason" required minLength={3} maxLength={500} placeholder="记录本次调整原因" /></label>
          <button className="button button--primary" type="submit" disabled={update.isPending}><Save size={16} />{update.isPending ? "正在保存…" : "保存并生效"}</button>
        </div> : <p className="transcode-settings__readonly">当前账号只有查看权限。</p>}
      </section>
    </form>}
  </div>;
}

function nodeTypeName(types: NodeType[] | undefined, id: string) {
  return types?.find((type) => type.id === id)?.display_name ?? id;
}
